import { NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, input } from "@angular/core";
import { DateTime, getCurrentTimezone } from "@ankaadia/ankaadia-shared";
import { KpiResultFragment, KpiType, KpiUnit } from "@ankaadia/graphql";
import { TranslocoService } from "@jsverse/transloco";
import { EnumPipe } from "../../../shared/pipes/enum.pipe";
import { DateFormatterService } from "../../../shared/services/date-formatter.service";

@Component({
  selector: "new-ui-kpi-card",
  templateUrl: "./new-ui-kpi-card.component.html",
  styleUrl: "./new-ui-kpi-card.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, EnumPipe],
})
export class NewUiKpiCardComponent {
  readonly kpi = input.required<KpiResultFragment>();
  private readonly transloco = inject(TranslocoService);
  private readonly dateFormatter = inject(DateFormatterService);
  readonly language = this.transloco.getActiveLang();

  // KPIs that should use linear interpolation for delta calculation
  private readonly interpolatedKpiTypes = [
    KpiType.AccountsReceivableNet,
    KpiType.AccountsReceivableCandidate,
    KpiType.AccountsReceivableLicense,
    KpiType.AccountsReceivableAddOnService,
    KpiType.CandidateMatched,
    KpiType.CandidateOnContingent,
  ];

  // Determine if this is a primary KPI based on the type
  get isPrimary(): boolean {
    const primaryTypes = [
      KpiType.CandidateActive,
      KpiType.OrganizationLicensed,
      KpiType.UserActive,
      KpiType.RevenueCandidate,
      KpiType.AccountsReceivableCandidate,
    ];
    return primaryTypes.includes(this.kpi().type);
  }

  // Determine if this is a total KPI based on the type
  get isTotal(): boolean {
    const totalTypes = [KpiType.CandidateTotal, KpiType.OrganizationTotal, KpiType.UserTotal, KpiType.RevenueTotal];
    return totalTypes.includes(this.kpi().type);
  }

  getKpiIcon(type: KpiType): string {
    const iconMap: Record<KpiType, string> = {
      [KpiType.CandidateActive]: "pi pi-user-plus brand-green",
      [KpiType.CandidateInactive]: "pi pi-user-minus brand-green",
      [KpiType.CandidateMatched]: "pi pi-user-check brand-green",
      [KpiType.CandidateOnContingent]: "pi pi-user-clock brand-green",
      [KpiType.CandidateTotal]: "pi pi-users brand-green",
      [KpiType.OrganizationLicensed]: "pi pi-verified brand-green",
      [KpiType.OrganizationTotal]: "pi pi-building brand-green",
      [KpiType.OrganizationWithActiveUser]: "pi pi-users brand-green",
      [KpiType.RevenueLicense]: "pi pi-key brand-green",
      [KpiType.RevenueAddOnService]: "pi pi-shopping-cart brand-green",
      [KpiType.RevenueCandidate]: "pi pi-wallet brand-green",
      [KpiType.RevenueCreditNote]: "pi pi-money-bill brand-green",
      [KpiType.RevenueTotal]: "pi pi-chart-line brand-green",
      [KpiType.AccountsReceivableLicense]: "pi pi-key brand-green",
      [KpiType.AccountsReceivableAddOnService]: "pi pi-shopping-cart brand-green",
      [KpiType.AccountsReceivableCandidate]: "pi pi-wallet brand-green",
      [KpiType.AccountsReceivableCreditNote]: "pi pi-money-bill brand-green",
      [KpiType.AccountsReceivableNet]: "pi pi-chart-line brand-green",
      [KpiType.UserTotal]: "pi pi-users brand-green",
      [KpiType.UserActive]: "pi pi-user-plus brand-green",
    };
    return iconMap[type] || "pi pi-chart-bar brand-green";
  }

  formatValue(kpi: KpiResultFragment): string {
    if (kpi.unitType === KpiUnit.Currency) {
      const currency = kpi.units || "EUR";
      return new Intl.NumberFormat(this.language, {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(kpi.value);
    }
    return new Intl.NumberFormat(this.language).format(kpi.value);
  }

  // Check if KPI should use interpolation
  shouldUseInterpolation(kpi: KpiResultFragment): boolean {
    // This is strange! Receiving a number instead of a Date here....

    /* eslint-disable-next-line prefer-custom-date-types/prefer-custom-date-types */
    const endOfMonth = new Date(kpi.endOfMonth as any as number);

    /* eslint-disable-next-line prefer-custom-date-types/prefer-custom-date-types */
    return this.interpolatedKpiTypes.includes(kpi.type) && endOfMonth.getTime() > new Date().getTime();
  }

  // Calculate interpolation weight based on current date
  getInterpolationWeight(): { weight: number; daysPassed: number; totalDays: number } {
    const now = DateTime.now(getCurrentTimezone());
    const { year, monthIndex, day } = now.getLocalComponents();

    // Get total days in current month

    /* eslint-disable-next-line prefer-custom-date-types/prefer-custom-date-types */
    const totalDays = new Date(year, monthIndex + 1, 0).getDate();

    // Treat current day as already passed
    const daysPassed = day;
    const weight = daysPassed / totalDays;

    return { weight, daysPassed, totalDays };
  }

  // Get interpolated comparison value
  getComparisonValue(kpi: KpiResultFragment): number {
    if (kpi.valueEndOfLastMonth === null || kpi.valueEndOfLastMonth === undefined) {
      return 0;
    }

    if (this.shouldUseInterpolation(kpi)) {
      const { weight } = this.getInterpolationWeight();
      return kpi.valueEndOfLastMonth * weight;
    }

    return kpi.valueEndOfLastMonth;
  }

  // Get last month name
  getLastMonthName(): string {
    const now = DateTime.now(getCurrentTimezone());
    const { year, monthIndex } = now.getLocalComponents();

    const lastMonth = DateTime.fromLocalParts({ year, monthIndex: monthIndex - 1, day: 1 });

    return this.dateFormatter.transformDateTime(lastMonth, { month: "long", year: "numeric" });
  }

  getDelta(kpi: KpiResultFragment): number | null {
    if (kpi.valueEndOfLastMonth === null || kpi.valueEndOfLastMonth === undefined) {
      return null;
    }
    const comparisonValue = this.getComparisonValue(kpi);
    return kpi.value - comparisonValue;
  }

  getDeltaPercentage(kpi: KpiResultFragment): number | null {
    const delta = this.getDelta(kpi);
    const comparisonValue = this.getComparisonValue(kpi);
    if (delta === null || comparisonValue === 0) {
      return null;
    }
    return (delta / comparisonValue) * 100;
  }

  formatDeltaAndPercentage(kpi: KpiResultFragment): string {
    const deltaStr = this.formatDelta(kpi);
    const percentageStr = this.formatDeltaPercentage(kpi);
    const suffix = this.shouldUseInterpolation(kpi) ? "*" : "";
    if (!deltaStr) {
      return "";
    }

    return [deltaStr, percentageStr ? ` (${percentageStr})` : null, suffix].filter((part) => part !== null).join("");
  }

  formatDelta(kpi: KpiResultFragment): string {
    const delta = this.getDelta(kpi);
    if (delta === null) {
      return "";
    }

    const sign = delta >= 0 ? "+" : "";
    if (kpi.unitType === KpiUnit.Currency) {
      const currency = kpi.units || "EUR";
      return (
        sign +
        new Intl.NumberFormat(this.language, {
          style: "currency",
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(delta)
      );
    }
    return sign + new Intl.NumberFormat(this.language).format(delta);
  }

  formatDeltaPercentage(kpi: KpiResultFragment): string {
    const percentage = this.getDeltaPercentage(kpi);
    if (percentage === null) {
      return "";
    }
    const sign = percentage >= 0 ? "+" : "";
    const formattedPercentage = new Intl.NumberFormat(this.language, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(percentage);
    return `${sign}${formattedPercentage} %`;
  }

  getDeltaClass(kpi: KpiResultFragment): string {
    const delta = this.getDelta(kpi);
    if (delta === null || delta === 0) {
      return "delta-neutral";
    }
    return delta > 0 ? "delta-positive" : "delta-negative";
  }

  getDeltaIcon(kpi: KpiResultFragment): string {
    const delta = this.getDelta(kpi);
    if (delta === null || delta === 0) {
      return "pi pi-minus";
    }
    return delta > 0 ? "pi pi-arrow-up" : "pi pi-arrow-down";
  }

  // Format value for tooltip
  formatValueForTooltip(value: number, kpi: KpiResultFragment): string {
    if (kpi.unitType === KpiUnit.Currency) {
      const currency = kpi.units || "EUR";
      return new Intl.NumberFormat(this.language, {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }
    return new Intl.NumberFormat(this.language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  // Get tooltip content
  getTooltipContent(kpi: KpiResultFragment): string {
    if (kpi.valueEndOfLastMonth === null || kpi.valueEndOfLastMonth === undefined) {
      return "";
    }

    const lastMonthName = this.getLastMonthName();
    const lastMonthValue = this.formatValueForTooltip(kpi.valueEndOfLastMonth, kpi);

    if (this.shouldUseInterpolation(kpi)) {
      const { weight, daysPassed, totalDays } = this.getInterpolationWeight();
      const comparisonValue = this.getComparisonValue(kpi);
      const weightPercentage = (weight * 100).toFixed(1);
      const comparisonValueFormatted = this.formatValueForTooltip(comparisonValue, kpi);

      // Return plain text content - CSS will handle the styling
      return [
        this.t("kpi.comparedTo", { period: lastMonthName }),
        this.t("kpi.valueLastMonth", { value: lastMonthValue }),
        this.t("kpi.timeWeighting", { daysPassed, totalDays, percentage: weightPercentage }),
        this.t("kpi.weightedValue", { weightedValue: comparisonValueFormatted }),
        this.t("kpi.weightedValueCalculation", {
          totalValue: lastMonthValue,
          percentage: weightPercentage,
          weightedValue: comparisonValueFormatted,
        }),
      ].join(" | ");
    }

    return [
      this.t("kpi.comparedTo", { period: lastMonthName }),
      this.t("kpi.valueLastMonth", { value: lastMonthValue }),
    ].join(" | ");
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(key, params);
  }
}
